import { Suspense, useCallback, useEffect, useRef } from 'react';
import { Link as RouterLink, Outlet, useLocation, useSearchParams } from 'react-router';
import { HFAppShell, HFLogo, HFNavItem, HFSpinner, LogoutIcon } from '@hf/designSystem/v1';
import { useAccount } from '@core/utils/auth';
import { FEATURE_FLAG_KEYS, useFeatureFlags } from '@core/utils/flags';
import { LAST_OPENED_STORED, canonicalSpaPath, labelForPath, metaForPath } from '@core/utils/lastOpened';
import { usePreferences } from '@core/utils/prefs';
import { PATHS, ROUTES_META, type RouteMeta } from '@core/routes';

export function AppLayout({ companyAdmin }: { companyAdmin: boolean }) {
  const { user, logout } = useAccount();
  const { ready: flagsReady, isEnabled } = useFeatureFlags();
  const { prefs, prefsReady, updatePrefs } = usePreferences();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const trackedKeyRef = useRef<string | undefined>(undefined);
  const printMode = searchParams.get('print') === '1';

  const tearsheetsOn = flagsReady && isEnabled(FEATURE_FLAG_KEYS.tearsheets);
  const omsOn = flagsReady && isEnabled(FEATURE_FLAG_KEYS.oms) && user?.source !== 'internal';
  const productVisible = useCallback(
    (path: string) => {
      if (path === PATHS.tearsheets || path.startsWith(`${PATHS.tearsheets}/`)) return tearsheetsOn;
      if (path === PATHS.oms || path.startsWith(`${PATHS.oms}/`)) return omsOn;
      return true;
    },
    [tearsheetsOn, omsOn],
  );
  const routes = Object.values(ROUTES_META);
  const mainNav = routes.filter((r) => r.nav === 'main' && productVisible(r.path));
  const footerNav = routes.filter((r) => r.nav === 'footer' && (!r.adminOnly || companyAdmin));

  useEffect(() => {
    // One write per navigation — the ref also stops the prefs update from re-triggering this.
    if (!prefsReady || !prefs || trackedKeyRef.current === location.key) return;
    if (!productVisible(location.pathname)) return;

    const path = canonicalSpaPath(location.pathname);
    const label = path ? labelForPath(location.pathname) : undefined;
    if (!path || !label) return;

    trackedKeyRef.current = location.key;
    void updatePrefs({
      lastOpened: [{ path, label, openedAt: new Date().toISOString() }, ...prefs.lastOpened.filter((item) => item.path !== path)].slice(
        0,
        LAST_OPENED_STORED,
      ),
    });
  }, [prefs, prefsReady, location.key, location.pathname, updatePrefs, productVisible]);

  const isSelected = (path: string) =>
    path === PATHS.home ? location.pathname === PATHS.home : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const renderNavItem = (item: RouteMeta) => (
    <HFNavItem key={item.path} label={item.label} icon={item.icon} selected={isSelected(item.path)} component={RouterLink} to={item.path} />
  );

  const waitingForEval = !flagsReady && Boolean(metaForPath(location.pathname)?.featureFlag);
  const page = waitingForEval ? (
    <HFSpinner center sx={{ minHeight: '40vh' }} />
  ) : (
    <Suspense fallback={<HFSpinner center />}>
      <Outlet />
    </Suspense>
  );

  if (printMode) {
    return page;
  }

  // These SPAs bleed their tab bar across the whole main column and limit their own content width.
  const fullWidthPage = isSelected(PATHS.oms) || isSelected(PATHS.tearsheets);

  return (
    <HFAppShell
      logo={<HFLogo variant="wordMark" width={220} />}
      logoCollapsed={<HFLogo variant="globe" height={34} />}
      navCollapsed={prefsReady && prefs ? prefs.navCollapsed : undefined}
      onNavCollapsedChange={(navCollapsed) => {
        if (!prefsReady) return;
        void updatePrefs({ navCollapsed });
      }}
      pageMaxWidth={fullWidthPage ? false : undefined}
      navSlot={mainNav.map(renderNavItem)}
      footerNavSlot={
        <>
          {footerNav.map(renderNavItem)}
          <HFNavItem label="Log out" icon={<LogoutIcon />} onClick={logout} />
        </>
      }
    >
      {page}
    </HFAppShell>
  );
}
